import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Menu } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent } from '../components/ui/tabs';
import { AdminSidebar } from '../components/admin/AdminSidebar';
import { ADMIN_NAV } from '../components/admin/adminNav';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { CatalogGuardBanner } from '../components/admin/CatalogGuardBanner';
import { usePermissions, PermissionGate } from '../context/PermissionContext';
import { toast } from 'sonner';
import axios from 'axios';
import { ChatHistoryTab } from '../components/admin/ChatHistoryTab';
import { ProductSalesTab } from '../components/admin/ProductSalesTab';
import { InstallationPricingTab } from '../components/admin/InstallationPricingTab';
import { QuotesTab } from '../components/admin/QuotesTab';
import { InvoicesTab } from '../components/admin/InvoicesTab';
import { UsersTab } from '../components/admin/UsersTab';
import { SalesOrdersTab } from '../components/admin/SalesOrdersTab';
import { CustomersTab } from '../components/admin/CustomersTab';
import { DiscountTiersTab } from '../components/admin/DiscountTiersTab';
import { TaxRatesTab } from '../components/admin/TaxRatesTab';
import EmailSettingsTab from '../components/admin/EmailSettingsTab';
import CompanyProfileTab from '../components/admin/company/CompanyProfileTab';
import MyAccountTab from '../components/account/MyAccountTab';
import NotificationRecipientsTab from '../components/admin/company/NotificationRecipientsTab';
import SiteResetTab from '../components/admin/company/SiteResetTab';
import SetupChecklistTab from '../components/admin/company/SetupChecklistTab';
import EmailPreviewTab from '../components/admin/email/EmailPreviewTab';
import { SetupReadinessBanner } from '../components/admin/company/SetupReadinessBanner';
import { PaymentsTab } from '../components/admin/PaymentsTab';
import { PaymentsCenter, PAYMENTS_TABS } from '../components/admin/payments/PaymentsCenter';
import { ProjectsTab } from '../components/admin/ProjectsTab';
import { ScheduleTab } from '../components/admin/ScheduleTab';
import { InstallersTab } from '../components/admin/InstallersTab';
import { CampaignsTab } from '../components/admin/CampaignsTab';
import { InventoryTab } from '../components/admin/InventoryTab';
import { SuppliersTab } from '../components/admin/SuppliersTab';
import { ReorderSuggestionsTab } from '../components/admin/ReorderSuggestionsTab';
import { MailsTab } from '../components/admin/MailsTab';
import { VisualizationsTab } from '../components/admin/VisualizationsTab';
import { ShadeRegionsPanel } from '../components/admin/ShadeRegionsPanel';
import { FabricCollectionsTab } from '../components/admin/FabricCollectionsTab';
import { QuoteRequestsTab } from '../components/admin/QuoteRequestsTab';
import { QuoteSourcesTab } from '../components/admin/QuoteSourcesTab';
import { LeadReportTab } from '../components/admin/LeadReportTab';
import { ContactMessagesTab } from '../components/admin/ContactMessagesTab';
import CompanyUsersTab from '../components/admin/CompanyUsersTab';
import RoleDashboard from '../components/admin/RoleDashboard';
import CMSTab from '../components/admin/CMSTab';
import MeasurementProjectsTab from '../components/admin/MeasurementProjectsTab';
import SampleRequestsTab from '../components/admin/SampleRequestsTab';
import OrderStatusManager from '../components/admin/OrderStatusManager';
import { FabricColorsTab } from '../components/admin/FabricColorsTab';
import { FabricQuoteRequestsTab } from '../components/admin/FabricQuoteRequestsTab';
import { SharedDesignAnalyticsTab } from '../components/admin/SharedDesignAnalyticsTab';
import { PublitasSyncTab } from '../components/admin/PublitasSyncTab';
import { DWSyncSection } from '../components/admin/DWSyncSection';
import GalleryTab from '../components/admin/GalleryTab';
import ConsultationsTab from '../components/admin/ConsultationsTab';
import PricingTab from '../components/admin/PricingTab';
import WebsiteTab from '../components/admin/website/WebsiteTab';
import BlogTab from '../components/admin/BlogTab';
import HelpGuide from '../components/admin/HelpGuide';
import { HELP_TOPIC_IDS } from '../components/admin/helpContent';
import { HelpLink } from '../components/admin/HelpLink';
import { HelpQuickSearch } from '../components/admin/HelpQuickSearch';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Main Admin Page
export const AdminPage = () => {
  const { user, token, isAuthenticated } = useAuth();
  const { canView, loading: permissionsLoading, isAdmin, role, permissions } = usePermissions();
  
  // Get initial tab from URL params
  const getInitialTab = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'dashboard';
  };
  
  const [activeTab, setActiveTab] = React.useState(getInitialTab);
  const [newQuoteRequestsCount, setNewQuoteRequestsCount] = React.useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(() => localStorage.getItem('admin_sidebar_collapsed') === '1');
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const [pageHealthCount, setPageHealthCount] = React.useState(0);
  const badges = { quoteRequests: newQuoteRequestsCount, pageHealth: pageHealthCount };
  const [colorJump, setColorJump] = React.useState(null);
  const jumpToColor = React.useCallback((color) => { setColorJump({ ...color, _t: Date.now() }); }, []);

  const selectTab = React.useCallback((tab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(window.location.search);
    params.set('tab', tab);
    window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
  }, []);

  React.useEffect(() => { if (colorJump) selectTab('collections'); }, [colorJump, selectTab]);
  React.useEffect(() => { localStorage.setItem('admin_sidebar_collapsed', sidebarCollapsed ? '1' : '0'); }, [sidebarCollapsed]);
  React.useEffect(() => {
    const onPop = () => setActiveTab(getInitialTab());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  
  // Fetch new quote requests count for notification badge
  React.useEffect(() => {
    const fetchNewQuoteRequests = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API_URL}/admin/quote-requests?status=new`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNewQuoteRequestsCount(response.data.length);
      } catch (error) {
        console.error('Failed to fetch quote requests count:', error);
      }
    };
    
    fetchNewQuoteRequests();
    // Poll every 60 seconds for new requests
    const interval = setInterval(fetchNewQuoteRequests, 60000);
    return () => clearInterval(interval);
  }, [token]);

  // Website page-health badge (live builder pages with unpublished changes / failing SEO)
  React.useEffect(() => {
    if (!token) return;
    const load = () => axios.get(`${API_URL}/admin/website/health`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setPageHealthCount(r.data.counts?.total || 0)).catch(() => {});
    load();
    const onChange = () => load();
    window.addEventListener('website-pages-changed', onChange);
    const interval = setInterval(load, 120000);
    return () => { clearInterval(interval); window.removeEventListener('website-pages-changed', onChange); };
  }, [token]);

  // Redirect installers away from admin to their mobile page
  if (isAuthenticated && user?.role_id === 'role_installer') {
    window.location.href = '/installer';
    return null;
  }

  if (!isAuthenticated) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-serif mb-4">Admin Access Required</h2>
            <p className="text-neutral-600 mb-6">Please sign in to access the admin dashboard.</p>
            <Button onClick={() => window.location.href = '/login'} className="btn-primary">
              Sign In
            </Button>
          </div>
        </div>
      </Layout>
    );
  }
  
  // Show loading while permissions are being fetched
  if (permissionsLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-neutral-300 border-t-amber-600 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-neutral-600">Loading permissions...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Check if user has a company role OR is a legacy admin OR has any permissions
  // Also check user.role_id from auth context as a fallback for fresh logins
  const hasAnyPermission = Object.values(permissions || {}).some(level => level && level !== 'none');
  const hasAdminAccess = user?.is_admin || isAdmin || role || user?.role_id || hasAnyPermission;

  if (!hasAdminAccess) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-serif mb-4">Access Denied</h2>
            <p className="text-neutral-600 mb-6">You don't have admin privileges to access this page.</p>
            <Button onClick={() => window.location.href = '/'} className="btn-primary">
              Go to Home
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const activeItem = ADMIN_NAV.flatMap((c) => c.items.map((i) => ({ ...i, category: c.label }))).find((i) => i.value === activeTab);

  return (
    <Layout hideFooter>
      <div className="pt-20 min-h-screen bg-neutral-50" data-testid="admin-page">
        <div className="flex min-h-[calc(100vh-5rem)]">
          {/* Sidebar (desktop) */}
          <aside className="hidden lg:block sticky top-20 h-[calc(100vh-5rem)] bg-white border-r border-neutral-200 shrink-0">
            <AdminSidebar activeTab={activeTab} onSelect={selectTab} onSelectColor={jumpToColor} badges={badges} collapsed={sidebarCollapsed} onToggleCollapsed={() => setSidebarCollapsed((v) => !v)} />
          </aside>

          {/* Sidebar (mobile drawer) */}
          {mobileNavOpen && (
            <div className="fixed inset-0 z-40 lg:hidden" data-testid="mobile-nav-overlay">
              <div className="absolute inset-0 bg-black/40" onClick={() => setMobileNavOpen(false)} />
              <div className="absolute left-0 top-0 bottom-0 bg-white shadow-xl pt-20">
                <AdminSidebar activeTab={activeTab} onSelect={(v) => { selectTab(v); setMobileNavOpen(false); }} onSelectColor={(c) => { jumpToColor(c); setMobileNavOpen(false); }} badges={badges} collapsed={false} onToggleCollapsed={() => setMobileNavOpen(false)} />
              </div>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0 flex flex-col">
            <header className="sticky top-20 z-30 bg-white/90 backdrop-blur border-b border-neutral-200 px-4 sm:px-6 h-12 flex items-center gap-3" data-testid="admin-topbar">
              <button type="button" onClick={() => setMobileNavOpen(true)} className="lg:hidden p-1.5 rounded-md text-neutral-600 hover:bg-neutral-100" data-testid="mobile-nav-btn" aria-label="Open admin menu">
                <Menu className="w-5 h-5" />
              </button>
              <nav className="flex items-center gap-1.5 text-sm min-w-0" aria-label="Breadcrumb" data-testid="admin-breadcrumb">
                <span className="text-neutral-400">Admin Panel</span>
                {activeItem && <ChevronRight className="w-3.5 h-3.5 text-neutral-300" />}
                {activeItem && <span className="text-neutral-500 hidden sm:inline">{activeItem.category}</span>}
                {activeItem && <ChevronRight className="w-3.5 h-3.5 text-neutral-300 hidden sm:inline" />}
                {activeItem && <span className="font-medium text-neutral-900 truncate">{activeItem.label}</span>}
              </nav>
              {activeTab !== 'help-guide' && HELP_TOPIC_IDS.has(activeTab) && (
                <span className="ml-auto"><HelpLink topic={activeTab} label="Help" testId="topbar-help-link" className="text-neutral-600 font-medium" /></span>
              )}
            </header>
            <CatalogGuardBanner token={token} />
            <SetupReadinessBanner token={token} activeTab={activeTab} onNavigate={selectTab} />
            <HelpQuickSearch />

            <Tabs value={activeTab} onValueChange={selectTab} className="flex-1 p-4 sm:p-6">
            <div className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6 min-h-[60vh]">
              <TabsContent value="dashboard">
                <RoleDashboard onNavigate={selectTab} />
              </TabsContent>

              <TabsContent value="gallery">
                <GalleryTab token={token} />
              </TabsContent>

              <TabsContent value="visualizations">
                <VisualizationsTab token={token} />
              </TabsContent>

              <TabsContent value="shade-regions">
                <ShadeRegionsPanel token={token} />
              </TabsContent>

              <TabsContent value="colors">
                <FabricColorsTab token={token} />
              </TabsContent>

              <TabsContent value="collections">
                <FabricCollectionsTab token={token} jumpTo={colorJump} />
              </TabsContent>

              <TabsContent value="sample-requests" data-testid="sample-requests-content">
                <SampleRequestsTab token={token} />
              </TabsContent>

              <TabsContent value="catalog-sync" data-testid="catalog-sync-content">
                <PublitasSyncTab token={token} />
                <div className="mt-8 pt-8 border-t border-neutral-200">
                  <DWSyncSection token={token} />
                </div>
              </TabsContent>

              <TabsContent value="pricing">
                <PricingTab token={token} />
                <div className="mt-8 pt-8 border-t border-neutral-200">
                  <InstallationPricingTab token={token} />
                </div>
              </TabsContent>

              <TabsContent value="pages">
                <WebsiteTab token={token} />
              </TabsContent>

              <TabsContent value="content-cms" data-testid="content-cms-content">
                <CMSTab token={token} />
              </TabsContent>

              <TabsContent value="blog">
                <BlogTab token={token} />
              </TabsContent>

              <TabsContent value="consultations">
                <ConsultationsTab token={token} user={user} />
              </TabsContent>

              <TabsContent value="customers">
                <CustomersTab token={token} />
              </TabsContent>

              <TabsContent value="fabric-quotes" data-testid="fabric-quotes-content">
                <FabricQuoteRequestsTab token={token} />
              </TabsContent>

              <TabsContent value="quote-requests" data-testid="quote-requests-content">
                <QuoteRequestsTab token={token} onStatusUpdate={() => {
                  // Refresh the count when a status is updated
                  axios.get(`${API_URL}/admin/quote-requests?status=new`, {
                    headers: { Authorization: `Bearer ${token}` }
                  }).then(res => setNewQuoteRequestsCount(res.data.length)).catch(() => {});
                }} />
              </TabsContent>

              <TabsContent value="quote-sources" data-testid="quote-sources-content">
                <QuoteSourcesTab token={token} />
              </TabsContent>

              <TabsContent value="lead-report" data-testid="lead-report-content">
                <LeadReportTab token={token} />
              </TabsContent>

              <TabsContent value="contact-messages" data-testid="contact-messages-content">
                <ContactMessagesTab token={token} />
              </TabsContent>

              <TabsContent value="chat-history">
                <ChatHistoryTab token={token} />
              </TabsContent>

              <TabsContent value="product-sales">
                <ProductSalesTab token={token} />
              </TabsContent>

              <TabsContent value="quotes">
                <QuotesTab token={token} />
              </TabsContent>

              <TabsContent value="sales-orders">
                <SalesOrdersTab token={token} />
              </TabsContent>

              <TabsContent value="order-fulfillment">
                <OrderStatusManager token={token} />
              </TabsContent>

              <TabsContent value="invoices">
                <InvoicesTab token={token} />
              </TabsContent>

              <TabsContent value="payments">
                <PaymentsTab token={token} />
              </TabsContent>

              <TabsContent value="discount-tiers">
                <DiscountTiersTab token={token} />
              </TabsContent>

              {PAYMENTS_TABS.map((t) => (
                <TabsContent key={t} value={t}><PaymentsCenter tab={t} token={token} /></TabsContent>
              ))}

              <TabsContent value="tax-rates">
                <TaxRatesTab token={token} />
              </TabsContent>

              <TabsContent value="my-account">
                <MyAccountTab />
              </TabsContent>
              <TabsContent value="company-profile">
                <CompanyProfileTab token={token} />
              </TabsContent>

              <TabsContent value="notification-recipients">
                <NotificationRecipientsTab token={token} />
              </TabsContent>

              <TabsContent value="site-reset">
                <SiteResetTab token={token} />
              </TabsContent>

              <TabsContent value="setup-checklist">
                <SetupChecklistTab token={token} onNavigate={selectTab} />
              </TabsContent>

              <TabsContent value="email-settings">
                <EmailSettingsTab token={token} />
              </TabsContent>

              <TabsContent value="email-preview">
                <EmailPreviewTab token={token} />
              </TabsContent>

              <TabsContent value="projects">
                <ProjectsTab token={token} onNavigateToMeasurements={() => selectTab('measurements')} userRole={role || user?.role} />
              </TabsContent>

              <TabsContent value="measurements">
                <MeasurementProjectsTab token={token} />
              </TabsContent>

              <TabsContent value="schedule">
                <ScheduleTab token={token} />
              </TabsContent>

              <TabsContent value="installers">
                <InstallersTab token={token} />
              </TabsContent>

              <TabsContent value="campaigns">
                <CampaignsTab token={token} />
              </TabsContent>


              <TabsContent value="inventory">
                <InventoryTab token={token} />
              </TabsContent>

              <TabsContent value="suppliers">
                <SuppliersTab token={token} />
              </TabsContent>

              <TabsContent value="reorder">
                <ReorderSuggestionsTab token={token} />
              </TabsContent>

              <TabsContent value="mails">
                <MailsTab token={token} />
              </TabsContent>

              <TabsContent value="company-users">
                <CompanyUsersTab />
              </TabsContent>

              <TabsContent value="shared-design-analytics" data-testid="shared-design-analytics-content">
                <SharedDesignAnalyticsTab token={token} />
              </TabsContent>

              <TabsContent value="help-guide" data-testid="help-guide-content">
                <HelpGuide />
              </TabsContent>
            </div>
          </Tabs>
          </div>
        </div>
      </div>
    </Layout>
  );
};
